import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { SESSION_REFRESH_POLICY } from '@nova/types';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AuthenticationDataService, AuthenticationService } from 'app/core/authentication';
import { LoginService } from 'app/core/authentication/services/login.service';

import { SessionRefreshApiService } from './session-refresh-api.service';

@Injectable({
  providedIn: 'root'
})
export class SessionRefreshCoordinatorService {
  private static readonly SESSION_EXPIRED_ERROR = 'session_expired';

  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly loginService = inject(LoginService);

  private activitySubscription?: Subscription;
  private eventCleanups: (() => void)[] = [];
  private hasQualifyingActivity = false;
  private isRefreshing = false;
  private isRunning = false;
  private isSessionExpiryHandling = false;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private skippedRefresh = false;

  public start(): void {
    if (!this.authenticationDataService.getAccessToken()) {
      return;
    }

    this.isSessionExpiryHandling = false;

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    // TEMP(TEACH-5319 debug): remove before merge
    // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
    console.log('[session-refresh] started', this.expiryInfo());
    this.bindQualifyingActivity();
    this.scheduleNextRefreshCheck();
  }

  public stop(): void {
    this.clearRefreshTimer();
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.eventCleanups.forEach((cleanup) => { cleanup(); });
    this.eventCleanups = [];
    this.hasQualifyingActivity = false;
    this.isRefreshing = false;
    this.isRunning = false;
    this.skippedRefresh = false;
  }

  public recordNetworkActivity(url: string): void {
    if (url.endsWith('/Auth/RefreshToken')) {
      return;
    }

    this.recordQualifyingActivity();
  }

  public recordQualifyingActivity(): void {
    if (!this.isRunning || !this.authenticationDataService.getAccessToken()) {
      return;
    }

    this.hasQualifyingActivity = true;

    if (!this.skippedRefresh) {
      return;
    }

    if (this.isStoredSessionValid()) {
      this.refreshNow();
    }
    else {
      this.handleSessionExpired();
    }
  }

  public handleSessionExpired(failedRequestToken?: string | null): boolean {
    const latestToken = this.authenticationDataService.getAccessToken();
    const storedSessionValid = this.isStoredSessionValid();
    // TEMP(TEACH-5319 debug): remove before merge
    // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
    console.log('[session-refresh] handleSessionExpired', {
      storedSessionValid,
      tokenChangedSinceFailure: !!latestToken && latestToken !== failedRequestToken,
      alreadyHandling: this.isSessionExpiryHandling,
      ...this.expiryInfo()
    });

    if (latestToken
      && latestToken !== failedRequestToken
      && storedSessionValid) {
      return false;
    }

    if (this.isSessionExpiryHandling) {
      return true;
    }

    this.isSessionExpiryHandling = true;
    this.stop();
    // TODO(TEACH-5320): Show the Session Expired modal and track session-expiry analytics here.
    // Pass samlRedirect=false: a session timeout should clear auth + route to login WITHOUT the
    // SAML single-logout iframe (which bootstraps the old AngularJS app and floods the browser).
    this.authenticationService.logOut(false);

    return true;
  }

  private bindQualifyingActivity(): void {
    this.ngZone.runOutsideAngular(() => {
      this.eventCleanups = [
        this.bindDocumentEvent('keydown', (event) => {
          if (this.isInputActivityTarget(event.target)) {
            this.runRecordQualifyingActivity();
          }
        }),
        this.bindDocumentEvent('submit', () => { this.runRecordQualifyingActivity(); })
      ];
    });

    this.activitySubscription = this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => { this.recordQualifyingActivity(); });
  }

  private bindDocumentEvent(
    eventName: 'keydown' | 'submit',
    handler: (event: Event) => void): () => void {
    this.document.addEventListener(eventName, handler, true);

    return () => {
      this.document.removeEventListener(eventName, handler, true);
    };
  }

  private isInputActivityTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return target instanceof HTMLInputElement
      || target instanceof HTMLTextAreaElement
      || target instanceof HTMLSelectElement
      || target.isContentEditable;
  }

  private runRecordQualifyingActivity(): void {
    this.ngZone.run(() => {
      this.recordQualifyingActivity();
    });
  }

  private scheduleNextRefreshCheck(): void {
    this.clearRefreshTimer();

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimer = setTimeout(() => {
        this.ngZone.run(() => { this.runRefreshCheck(); });
      }, SESSION_REFRESH_POLICY.refreshCheckIntervalMs);
    });
  }

  private runRefreshCheck(): void {
    if (!this.isRunning) {
      return;
    }

    if (!this.hasQualifyingActivity) {
      this.skippedRefresh = true;
      // TEMP(TEACH-5319 debug): remove before merge
      // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
      console.log('[session-refresh] check: no qualifying activity -> skip', this.expiryInfo());
      this.scheduleNextRefreshCheck();

      return;
    }

    // TEMP(TEACH-5319 debug): remove before merge
    // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
    console.log('[session-refresh] check: activity present -> refresh', this.expiryInfo());
    this.refreshNow();
  }

  private refreshNow(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    this.clearRefreshTimer();
    const refreshRequestToken = this.authenticationDataService.getAccessToken();

    this.injector.get(SessionRefreshApiService).refreshToken()
      .subscribe({
        next: (tokenResponse) => {
          this.loginService.setTokenResponse(tokenResponse);
          this.hasQualifyingActivity = false;
          this.isRefreshing = false;
          this.skippedRefresh = false;
          // TEMP(TEACH-5319 debug): remove before merge
          // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
          console.log('[session-refresh] refresh OK', this.expiryInfo());
          this.scheduleNextRefreshCheck();
        },
        error: (error) => {
          this.isRefreshing = false;
          // TEMP(TEACH-5319 debug): remove before merge
          // eslint-disable-next-line no-console -- TEMP(TEACH-5319 debug)
          console.log('[session-refresh] refresh FAILED', {
            status: error instanceof HttpErrorResponse ? error.status : undefined,
            sessionExpired: this.isSessionExpiredError(error)
          });

          if (this.isSessionExpiredError(error)) {
            const didExpireSession = this.handleSessionExpired(refreshRequestToken);

            if (!didExpireSession) {
              this.hasQualifyingActivity = false;
              this.skippedRefresh = false;
              this.scheduleNextRefreshCheck();
            }

            return;
          }

          this.scheduleNextRefreshCheck();
        }
      });
  }

  private isSessionExpiredError(error: unknown): boolean {
    return error instanceof HttpErrorResponse
      && error.status === 401
      && error.error?.error === SessionRefreshCoordinatorService.SESSION_EXPIRED_ERROR;
  }

  private isStoredSessionValid(): boolean {
    const claims = this.authenticationDataService.getUserClaims();

    if (!claims?.expiry) {
      return false;
    }

    return DateTime.utc() < claims.expiry;
  }

  // TEMP(TEACH-5319 debug): remove before merge
  private expiryInfo(): { at: string; expiry: string | null; minutesToExpiry: number | null } {
    const expiry = this.authenticationDataService.getUserClaims()?.expiry;
    const minutesToExpiry = expiry
      ? Math.round(expiry.diff(DateTime.utc(), 'minutes').minutes)
      : null;

    return {
      at: DateTime.now().toFormat('HH:mm:ss'),
      expiry: expiry?.toISO() ?? null,
      minutesToExpiry
    };
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer === null) {
      return;
    }

    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}
